import type { SidebarItem, SidebarSubItem } from './types';

/**
 * Segment-aware active check: the location is `path` or a descendant of it. Avoids
 * a slug prefix-matching a sibling (e.g. `/programs/ryc-pro-collective` vs
 * `/programs/ryc-pro-collective-2`).
 */
const isPathActive = (pathname: string, path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

/**
 * Whether the item or any of its descendants has a path active for `pathname`.
 * Recurses because sub-items can be nested more than one level deep — feeds sit
 * under a non-navigable community-container folder, and modules under a space
 * folder — so a single-level check would miss those routes.
 */
const hasActiveDescendant = (item: SidebarItem | SidebarSubItem, pathname: string): boolean => {
  if (item.path && isPathActive(pathname, item.path)) {
    return true;
  }
  return (item.children ?? []).some(child => hasActiveDescendant(child, pathname));
};

/**
 * The nav item whose submenu panel belongs to the current route — the item whose
 * own path or any descendant path prefixes the pathname (Programs / Community).
 * Returns undefined on routes without a submenu (Dashboard, billing, ...), where
 * the panel and its toggle are not shown.
 */
export const findPanelItem = (items: SidebarItem[], pathname: string): SidebarItem | undefined =>
  items.find(item => {
    if (!item.children || item.children.length === 0) {
      return false;
    }

    if (hasActiveDescendant(item, pathname)) {
      return true;
    }

    // Special case for Community: match any /community-hub/spaces/{id}/community path
    if (item.id === 'community') {
      const communityRoutePattern = /\/community-hub\/spaces\/[^/]+\/community/;
      return communityRoutePattern.test(pathname);
    }

    return false;
  });
